
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import ClientsApi from "@/api/clients-api";
import { isDecimalNumber } from "@/helpers/data";

const CREDIT_PAYMENT_TYPE_ID = -1;

type PaymentLine = {
  key: number;
  client: any;
  payment_type: any;
  amount: string | number;
  allocated_amount: number;
  credit_deposit_amount: number;
  notes: string;
};

@Component
export default class PaymentAllocation extends Vue {
  @Prop({ required: true })
  clients!: any[];

  @Prop({ required: true })
  paymentTypes!: any[];

  @Prop({ required: true })
  balance!: number;

  paymentLines: PaymentLine[] = [];
  nextPaymentLineKey = 1;
  clientCredits: Record<string, number> = {};
  creditLoadingByClient: Record<string, boolean> = {};
  creditLoadFailed: Record<string, boolean> = {};
  loadedCreditClients: Record<string, boolean> = {};
  showValidationErrors = false;
  creditPaymentTypeId = CREDIT_PAYMENT_TYPE_ID;

  clientRule = [
    (val: any) =>
      (val && val.name && val.name.length > 0) ||
      this.$i18n.t("client_required"),
  ];

  paymentTypeRule = [
    (val: any) =>
      (val && (val.id > 0 || val.id === CREDIT_PAYMENT_TYPE_ID)) ||
      this.$i18n.t("payment_type_required"),
  ];

  notesRule = [
    (val: any) => {
      if (val != null)
        return val.length <= 1000 || this.$i18n.t("form_notes_length_rule");
      else return true;
    },
  ];

  created() {
    this.ensurePaymentLine();
  }

  async mounted() {
    await this.loadClientCredits();
    this.recalculateAllocation();
  }

  @Watch("balance")
  onBalanceChanged() {
    this.recalculateAllocation();
  }

  @Watch("clients", { deep: true })
  async onClientsChanged() {
    this.ensurePaymentLine();
    await this.loadClientCredits();
    this.recalculateAllocation();
  }

  @Watch("paymentTypes", { deep: true })
  onPaymentTypesChanged() {
    this.paymentLines.forEach((line) => {
      if (!line.payment_type && this.paymentTypes.length) {
        line.payment_type = this.paymentTypes[0];
      }
    });
    this.emitPayments();
  }

  get totalAppliedCredit() {
    return this.roundMoney(this.paymentLines.reduce((total, line) => total + Math.max(this.appliedCredit(line), 0), 0));
  }

  get totalJobAmount() {
    return this.roundMoney(this.paymentLines.reduce((total, line) => total + this.numberValue(line.allocated_amount), 0));
  }

  get totalCreditAmount() {
    return this.roundMoney(this.paymentLines.reduce((total, line) => total + this.numberValue(line.credit_deposit_amount), 0));
  }

  get paymentBreakdown() {
    const cashByType: Record<string, number> = {};
    this.paymentLines.forEach((line) => {
      if (this.isCreditLine(line)) return;
      const cashToJob = this.roundMoney(this.numberValue(line.allocated_amount));
      if (cashToJob <= 0) return;
      const label = line.payment_type ? line.payment_type.payment_type : "-";
      cashByType[label] = this.roundMoney((cashByType[label] || 0) + cashToJob);
    });

    const parts = Object.keys(cashByType).map((label) => `${this.formatCurrency(cashByType[label])} ${label}`);
    if (this.totalAppliedCredit > 0) {
      parts.push(String(this.$t("paymentBreakdownCredit", { amount: this.formatCurrency(this.totalAppliedCredit) })));
    }

    return parts.length > 1 || this.totalAppliedCredit > 0 ? parts.join(" + ") : "";
  }

  get remainingAmount() {
    return this.roundMoney(Math.max(this.balance - this.totalJobAmount, 0));
  }

  get validationErrors() {
    const errors: string[] = [];
    const appliedByClient: Record<string, number> = {};

    this.paymentLines.forEach((line) => {
      if (!line.client) {
        errors.push(String(this.$t("client_required")));
      }

      if (!line.payment_type || !(line.payment_type.id > 0 || this.isCreditLine(line))) {
        errors.push(String(this.$t("payment_type_required")));
      }

      if (this.numberValue(line.amount) < 0) {
        errors.push(String(this.$t("nonZeroAmountRuleText")));
      }

      if (this.numberValue(line.amount) <= 0) {
        errors.push(String(this.$t("nonZeroAmountRequired")));
      }

      if (line.notes.length > 1000) {
        errors.push(String(this.$t("form_notes_length_rule")));
      }

      if (line.client) {
        const clientId = String(line.client.id);
        if (this.creditLoadFailed[clientId]) {
          errors.push(String(this.$t("creditLoadFailed")));
        }
        appliedByClient[clientId] =
          (appliedByClient[clientId] || 0) + this.appliedCredit(line);
      }
    });

    Object.keys(appliedByClient).forEach((clientId) => {
      if (
        appliedByClient[clientId] >
        (this.clientCredits[clientId] || 0) + 0.001
      ) {
        errors.push(String(this.$t("availableCreditAmountRuleText")));
      }
    });

    if (this.totalAppliedCredit > this.balance + 0.001) {
      errors.push(String(this.$t("creditExceedsJobBalance")));
    }

    return Array.from(new Set(errors));
  }

  ensurePaymentLine() {
    if (this.paymentLines.length || !this.clients.length) {
      return;
    }

    this.paymentLines.push(this.createPaymentLine(this.clients[0]));
  }

  createPaymentLine(client: any): PaymentLine {
    const line = {
      key: this.nextPaymentLineKey,
      client,
      payment_type: this.paymentTypes[0],
      amount: "",
      allocated_amount: 0,
      credit_deposit_amount: 0,
      notes: "",
    };
    this.nextPaymentLineKey += 1;
    return line;
  }

  async loadClientCredits() {
    const validClients = this.clients.filter((client) => client && client.id != null);

    await Promise.all(
      validClients.map(async (client) => {
        const clientId = String(client.id);
        if (this.loadedCreditClients[clientId] || this.creditLoadingByClient[clientId]) {
          return;
        }

        this.$set(this.creditLoadingByClient, clientId, true);

        try {
          const credit = await new ClientsApi().getClientCredit(clientId);
          this.$set(this.clientCredits, clientId, Number(credit?.available_credit) || 0);
          this.$set(this.loadedCreditClients, clientId, true);
          this.$delete(this.creditLoadFailed, clientId);
        } catch {
          this.$set(this.creditLoadFailed, clientId, true);
        } finally {
          this.$set(this.creditLoadingByClient, clientId, false);
        }
      })
    );
  }

  onlyDecimalNumbers(evt: any) {
    isDecimalNumber(evt);
  }

  paymentLineAmountRules(line: PaymentLine) {
    return [
      () =>
        !line.client || !this.creditLoadFailed[String(line.client.id)] || this.$i18n.t("creditLoadFailed"),
      (val: string | number) =>
        this.numberValue(val) > 0 || this.$i18n.t("nonZeroAmountRequired"),
      (val: string | number) =>
        this.numberValue(val) >= 0 || this.$i18n.t("nonZeroAmountRuleText"),
      () =>
        !this.isCreditLine(line) ||
        this.appliedCreditForClient(line) <= this.availableCredit(line) + 0.001 ||
        this.$i18n.t("availableCreditAmountRuleText"),
      () =>
        !this.isCreditLine(line) ||
        this.totalAppliedCredit <= this.balance + 0.001 ||
        this.$i18n.t("creditExceedsJobBalance"),
    ];
  }

  addPayer() {
    this.paymentLines.push(this.createPaymentLine(this.clients[0]));
    this.recalculateAllocation();
  }

  removePayer(index: number) {
    this.paymentLines.splice(index, 1);
    this.recalculateAllocation();
  }

  payerChanged(line: PaymentLine) {
    if (this.isCreditLine(line)) {
      line.payment_type = this.paymentTypes[0];
    }
    this.recalculateAllocation();
  }

  get creditPaymentType() {
    return { id: CREDIT_PAYMENT_TYPE_ID, payment_type: String(this.$t("fromCreditPaymentType")) };
  }

  isCreditLine(line: PaymentLine) {
    return Boolean(line.payment_type) && line.payment_type.id === CREDIT_PAYMENT_TYPE_ID;
  }

  paymentTypeItems(line: PaymentLine) {
    const canUseCredit = this.isCreditLine(line) || (this.balance > 0 && this.creditLeftForLine(line) > 0);
    return canUseCredit ? [this.creditPaymentType, ...this.paymentTypes] : this.paymentTypes;
  }

  useCredit(line: PaymentLine) {
    line.payment_type = this.creditPaymentType;
    this.recalculateAllocation();
  }

  repaymentAmount(line: PaymentLine) {
    const paidByOtherLines = this.paymentLines.reduce(
      (total, currentLine) => currentLine === line ? total : total + this.appliedCredit(currentLine) + this.externalAmount(currentLine),
      0
    );
    const owed = Math.max(this.balance - paidByOtherLines, 0);
    if (!this.isCreditLine(line)) {
      return this.roundMoney(owed);
    }

    const creditUsedByOtherLines = this.paymentLines.reduce((total, currentLine) => {
      if (currentLine === line || !currentLine.client || !line.client || currentLine.client.id !== line.client.id) return total;
      return total + this.appliedCredit(currentLine);
    }, 0);
    return this.roundMoney(Math.min(owed, Math.max(this.availableCredit(line) - creditUsedByOtherLines, 0)));
  }

  repayRemaining() {
    const lastLine = this.paymentLines[this.paymentLines.length - 1];
    if (lastLine && this.numberValue(lastLine.amount) <= 0) {
      this.repayment(lastLine);
      return;
    }

    const line = this.createPaymentLine(lastLine ? lastLine.client : this.clients[0]);
    line.amount = this.remainingAmount;
    this.paymentLines.push(line);
    this.recalculateAllocation();
  }

  repayment(line: PaymentLine) {
    line.amount = this.repaymentAmount(line);
    this.recalculateAllocation();
  }

  creditLeftForLine(line: PaymentLine) {
    const index = this.paymentLines.indexOf(line);
    const usedByEarlierLines = this.paymentLines.slice(0, index).reduce((total, currentLine) => {
      if (!currentLine.client || !line.client || currentLine.client.id !== line.client.id) return total;
      return total + this.appliedCredit(currentLine);
    }, 0);
    return this.roundMoney(Math.max(this.availableCredit(line) - usedByEarlierLines, 0));
  }

  availableCredit(line: PaymentLine) {
    return line.client ? this.clientCredits[String(line.client.id)] || 0 : 0;
  }

  isCreditLoading(line: PaymentLine) {
    return line.client ? Boolean(this.creditLoadingByClient[String(line.client.id)]) : false;
  }

  appliedCredit(line: PaymentLine) {
    return this.isCreditLine(line) ? Math.max(this.numberValue(line.amount), 0) : 0;
  }

  externalAmount(line: PaymentLine) {
    return this.isCreditLine(line) ? 0 : Math.max(this.numberValue(line.amount), 0);
  }

  appliedCreditForClient(line: PaymentLine) {
    if (!line.client) {
      return 0;
    }

    return this.paymentLines.reduce((total, currentLine) => {
      if (!currentLine.client || currentLine.client.id !== line.client.id) {
        return total;
      }
      return total + this.appliedCredit(currentLine);
    }, 0);
  }

  recalculateAllocation() {
    let remainingForExternalPayments = Math.max(this.balance - this.totalAppliedCredit, 0);

    this.paymentLines.forEach((line) => {
      const externalAmount = this.externalAmount(line);
      const externalJobAmount = Math.min(externalAmount, remainingForExternalPayments);

      line.allocated_amount = this.roundMoney(this.appliedCredit(line) + externalJobAmount);
      line.credit_deposit_amount = this.roundMoney(externalAmount - externalJobAmount);
      remainingForExternalPayments = this.roundMoney(Math.max(remainingForExternalPayments - externalJobAmount, 0));
    });

    this.emitPayments();
  }

  numberValue(value: string | number) {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  }

  roundMoney(value: number) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  formatCurrency(value: number) {
    return `${this.numberValue(value).toFixed(2)} €`;
  }

  buildPayments() {
    const payments: JobPaymentRequestDto[] = [];

    this.paymentLines.forEach((line) => {
      payments.push({
        client_id: line.client ? line.client.id : null,
        payment_type_id: line.payment_type && !this.isCreditLine(line) ? line.payment_type.id : null,
        amount: this.externalAmount(line),
        credit_deposit_amount: this.numberValue(line.credit_deposit_amount),
        apply_credit_amount: this.appliedCredit(line),
        notes: line.notes,
      });
    });

    return payments;
  }

  emitPayments() {
    this.$emit("update-payments", this.buildPayments());
  }

  validate() {
    this.showValidationErrors = true;
    this.recalculateAllocation();
    return this.validationErrors.length === 0;
  }
}
